import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import { useLocale } from '@adsk/alloy-react-locale';
import { useDataApi } from '@adsk/alloy-helpers';
import { useProjectEntitlements, useUser } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { buildRemoveMemberRequest, getProjectListPageUrl } from '../../utils';

const RemoveRoleAndMemberConfirmationModal = ({
  member,
  onClose,
  onRemove,
  show,
  useConstructionApis,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { currentProject } = useProjectEntitlements();
  const userData = useUser();
  const latestOnRemove = useRef();
  latestOnRemove.current = onRemove;
  const {
    data: removeMemberData,
    error: removeMemberError,
    loading: isRemovingMember,
    doFetch: doFetchRemoveMember,
  } = useDataApi();
  const currentUser = userData?.user;

  const handleConfirmRemoveMember = () => {
    doFetchRemoveMember(
      ...buildRemoveMemberRequest(
        currentProject.id,
        member.id,
        useConstructionApis
      )
    );
  };

  useEffect(() => {
    if (!removeMemberData) return;

    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('REMOVE_MEMBER_SUCCESS', {
        name: member.name,
        email: member.email,
      }),
    });
    if (latestOnRemove.current) latestOnRemove.current(true);
    if (currentUser.id === member.id && !member.accessLevels.accountAdmin) {
      window.location.assign(getProjectListPageUrl());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberData]);

  useEffect(() => {
    if (!removeMemberError) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('CHANGES_NOT_SAVED', {
        field: T.translate('ROLE_LABEL'),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberError]);

  return (
    <ModalConfirmation
      isConfirmationButtonAlert
      isOnConfirmInProgress={isRemovingMember}
      message={T.translate('REMOVE_ROLE_AND_MEMBER_CONFIRMATION_MODAL.BODY')}
      onClose={onClose}
      onConfirm={handleConfirmRemoveMember}
      show={!!show}
      title={T.translate('REMOVE_ROLE_AND_MEMBER_CONFIRMATION_MODAL.TITLE')}
    />
  );
};

RemoveRoleAndMemberConfirmationModal.propTypes = {
  member: PropTypes.shape({
    accessLevels: PropTypes.objectOf(PropTypes.bool),
    email: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  show: PropTypes.bool,
  useConstructionApis: PropTypes.bool,
};

RemoveRoleAndMemberConfirmationModal.defaultProps = {
  show: false,
  useConstructionApis: false,
};

export default RemoveRoleAndMemberConfirmationModal;
